import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as PropTypes from 'prop-types';

import get from 'lodash/get';
import isNull from 'lodash/isNull';
import browserHistory from 'react-router/lib/browserHistory';
import { redirectToUsers } from '../../../utils/appointments/redirect-utils';
import ListWrapper from '../Components/ListWrapper';
import Loader from '../../common/Loader';
import {
  HEALTHBOARD_TODO,
  HEALTHBOARD_APPOINTMENTS,
  HEALTHBOARD,
} from '../../../constants/routes';
import {
  printListTitle,
  getSurveyEmptyBlockData,
  getFirstName,
} from '../utils';
import { selectDependentStatusById } from '../../../redux/profiles/selectors';
import AppointmentList from '../Components/AppointmentList';
import {
  DEPENDENT_FORM_SUBMIT_REDIRECT,
  SURVEYS_LIST_TYPE,
} from '../../../constants';
import { useCareUsers } from '../useCareUsers';
import EmptyBlock from '../Components/EmptyBlock';
import TodoList from '../Components/TodoList';
import { enhance } from './enhance';
import MetaHelmet from '../../AppHelmet/MetaHelmet';
import { usePageView } from '../../../hocs/usePageView';
import { CustomEvents, GATrackCustomEvent } from '../../../gtag';
import RedirectToMozzazButton from './components/RedirectToMozzazButton';
import { selectAppointmentSchedulingAllowed } from '../../../redux/data/selectors';
import AppointmentListWrapper from './components/AppointmentListWrapper';

function HealthBoardHome({
  appointmentList,
  appointmentLoading,
  completedTasksCount,
  dependentsList,
  fetchScheduledTasks,
  fetchUpcomingAppointments,
  historyAvailableScheduledTasks,
  historyAvailableUpcomingList,
  loggedUserId,
  pending,
  scheduleTasks,
  scheduleTasksCount,
  setTab,
  setDependentSubmitRedirect,
  upcomingAppointmentsCount,
  userId,
  intl: { formatMessage },
}) {
  const id = Number(userId);
  const { getTargetUser } = useCareUsers();
  const dependentStatus = useSelector(selectDependentStatusById(id));
  const isAppointmentSchedulingAllowed = useSelector(
    selectAppointmentSchedulingAllowed,
  );
  const targetUser = getTargetUser(id);

  usePageView('Home');
  useEffect(() => {
    fetchUpcomingAppointments({ id, offset: 0, limit: 3 });
    fetchScheduledTasks({ id, offset: 0, limit: 3 });
  }, [userId]);

  const { text: surveysActionText, tab: todoTab } = printListTitle({
    upcomingCounter: scheduleTasksCount,
    formatMessage,
    upcomingTab: SURVEYS_LIST_TYPE.UPCOMING,
    completedTab: SURVEYS_LIST_TYPE.RESULTS,
    historyAvailable: historyAvailableScheduledTasks,
  });

  const { text: appointmentActionText } = printListTitle({
    upcomingCounter: upcomingAppointmentsCount,
    formatMessage,
    historyAvailable: historyAvailableUpcomingList,
  });

  const surveysEmptyBlockData = getSurveyEmptyBlockData(
    dependentStatus,
    scheduleTasksCount,
    completedTasksCount,
    formatMessage,
  );

  const handleAppointmentListActionClick = () => {
    browserHistory.push(`${HEALTHBOARD}/${userId}/${HEALTHBOARD_APPOINTMENTS}`);
  };

  const getListTitle = messageId => {
    const prefix =
      id === loggedUserId
        ? formatMessage({ id: 'common.my' })
        : getFirstName(targetUser);
    return `${prefix} ${formatMessage({ id: messageId })}`;
  };

  const handleGetCare = () => {
    setDependentSubmitRedirect(DEPENDENT_FORM_SUBMIT_REDIRECT.GET_CARE);
    redirectToUsers(id, false);
    GATrackCustomEvent(CustomEvents.GET_CARE_START);
  };

  const getButtonText = () => {
    if (id === loggedUserId) {
      return isAppointmentSchedulingAllowed
        ? formatMessage({ id: 'appointments.seeAProvider' })
        : null;
    }

    const activeDependent = dependentsList.find(
      ({ dependent }) => dependent.id === id,
    );

    const isAppointmentSchedulingAllowedMetadata = get(
      activeDependent,
      'metadata.appointmentSchedulingAllowed',
      null,
    );

    if (!activeDependent && isAppointmentSchedulingAllowed) {
      return formatMessage({ id: 'appointments.seeAProvider' });
    }

    return isAppointmentSchedulingAllowedMetadata
      ? formatMessage({ id: 'appointments.seeAProvider' })
      : null;
  };

  const appointmentListTitle = useMemo(() => {
    const title = getListTitle('healthboard.title.appointments');

    const counter = appointmentLoading ? null : upcomingAppointmentsCount;

    const counterText = !isNull(counter) ? `(${counter})` : '';

    return `${title} ${counterText}`;
  }, [appointmentLoading, upcomingAppointmentsCount]);

  if (!targetUser) return <Loader />;

  return (
    <div className="pl2 pr2 overflow-y-auto">
      <MetaHelmet title="pages.title.home" />

      <ListWrapper
        title={getListTitle('chat.titles.activitiesChatTab')}
        counter={pending ? null : scheduleTasksCount}
        actionText={pending ? null : surveysActionText}
        redirectTo={`${HEALTHBOARD}/${userId}/${HEALTHBOARD_TODO}`}
        additionalAction={() => setTab(todoTab)}
      >
        {pending && <Loader className="mt3" />}
        {!pending && (
          <If condition={Boolean(scheduleTasksCount)}>
            <TodoList list={scheduleTasks} />
            <Else />
            <EmptyBlock {...surveysEmptyBlockData} className="mt3" />
          </If>
        )}
      </ListWrapper>
      <AppointmentListWrapper
        actionText={appointmentLoading ? null : appointmentActionText}
        title={appointmentListTitle}
        additionalAction={handleAppointmentListActionClick}
        formatMessage={formatMessage}
        onClick={handleGetCare}
        buttonText={getButtonText()}
        renderAdditionalButton={
          <RedirectToMozzazButton
            btnTitle={formatMessage({ id: 'other.goToMozzaz' })}
          />
        }
      >
        <AppointmentList
          loading={appointmentLoading}
          list={appointmentList}
          id={userId}
          isUpcoming
        />
      </AppointmentListWrapper>
    </div>
  );
}

HealthBoardHome.propTypes = {
  appointmentList: PropTypes.array,
  appointmentLoading: PropTypes.bool,
  completedTasksCount: PropTypes.number,
  dependentsList: PropTypes.array,
  fetchCompletedTasks: PropTypes.func,
  fetchHistoryAppointments: PropTypes.func,
  fetchScheduledTasks: PropTypes.func,
  fetchUpcomingAppointments: PropTypes.func,
  historyAvailableScheduledTasks: PropTypes.bool,
  historyAvailableUpcomingList: PropTypes.bool,
  intl: PropTypes.object,
  loggedUserId: PropTypes.number,
  pending: PropTypes.bool,
  scheduleTasks: PropTypes.array,
  scheduleTasksCount: PropTypes.number,
  setTab: PropTypes.func,
  setDependentSubmitRedirect: PropTypes.func,
  targetUser: PropTypes.object,
  upcomingAppointmentsCount: PropTypes.number,
  userId: PropTypes.string,
};

export default enhance(HealthBoardHome);



// WEBPACK FOOTER //
// ./src/components/Healthboard/HealthboardHome/index.jsx